/** Editorul unui șablon de tronson — pagina lui, pe ecran plin.
 *
 *  PAGINĂ SEPARATĂ, nu un panou în colecție. Un editor de plan are nevoie de tot
 *  ecranul: paletă la stânga, proprietăți la dreapta, iar între ele o pânză pe
 *  care un modul de colț de 17,55 m trebuie să încapă la o scară la care se
 *  poate apuca un perete de 10 cm. Deschis într-un sertar, ar rămâne o fâșie.
 *  De-aia are și rută proprie — se poate da linkul mai departe și se poate
 *  reîncărca fără să se piardă ce editai.
 *
 *  CE SE SALVEAZĂ ȘI CÂND. «Salvează» scrie o CIORNĂ: o versiune nouă, legată de
 *  proiect, pe care motoarele n-o citesc. Ce nu se leagă vine înapoi ca listă și
 *  se arată sub desen, dar nu oprește salvarea — cineva are voie să lase un
 *  perete mutat pe jumătate. «Publică» trece prin poarta întreagă a catalogului
 *  și refuză când ceva nu se leagă, fiindcă `published` e ce citesc motoarele.
 *
 *  NIMIC DERIVAT NU SE CALCULEAZĂ AICI. Ariile de pe desen sunt de afișat;
 *  ariile care se scriu, `useful_m2` și `alpha` le pune serverul
 *  (`architecture/layout/tronson_edit.py`). Un al doilea loc care știe să
 *  calculeze o arie utilă e un al doilea loc care poate greși altfel.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as api from "../api";
import { useRolProiect } from "../components/rol";
import DialogRedenumire from "../components/DialogRedenumire";
import type { TronsonDoc } from "../viewer/tronson/doc";
import { Icoana } from "../viewer/tronson/icoane";
import ImparteModul from "../viewer/tronson/ImparteModul";
import TronsonNou from "../viewer/tronson/TronsonNou";
import MeniuExport, { type FormatExport } from "../viewer/tronson/export/MeniuExport";
import { exportaTronson } from "../viewer/tronson/export/exporta";
import { gataDeDeschis } from "../viewer/tronson/editor/model";
import { ComutatoareMasa, MasaDeLucru, useMasa }
  from "../viewer/tronson/editor/Masa";
import { antetul, areRandul, cereCiornaIntai, versiuneaDeBaza,
         versiuneaDePublicat, type Scriere }
  from "../viewer/tronson/editor/scriere";
import VersionPicker from "../viewer/tronson/editor/VersionPicker";
import { textulAvertismentului, type Avertisment } from "../viewer/tronson/editor/avertismente";
import { isMuted, setMuted, shouldAnnounce } from "../viewer/tronson/editor/warningsOnSave";
import { arata as arataToast } from "../toasts";
import { potPleca, tineNescrisul } from "../nescris";
import Asistent from "../asistent/Asistent";
import type { ContextEditor } from "../asistent/unelte";
import { data, ora, t as tr, tn, useT } from "../i18n";

/** CARE DIN CELE DOUĂ CEASURI se arată în colț, și dacă e scris sau nu.
 *
 *  Cerut în trei bucăți (arhitect, 21.10.2026): «să se actualizeze ora pe
 *  măsură ce editez; dacă salvăm, salvează acea oră și o ține minte; dacă nu
 *  salvăm, revine la ultima oră salvată».
 *
 *  A TREIA E TOCMAI MOTIVUL pentru care ceasul atingerii stă DEOPARTE de cel
 *  al bazei, în loc să-l înlocuiască: alegerea se face la fiecare randare, deci
 *  în clipa în care documentul se întoarce la cel scris — un ⌘Z până la capăt,
 *  o renunțare, o salvare — se arată iar al bazei, fără nimic de șters. Scris
 *  peste el, ora locală n-ar fi avut de unde să se întoarcă.
 *
 *  SCOASĂ DIN COMPONENTĂ ca să poată fi probată: pagina cere routerul și toate
 *  rutele API, iar regula asta e ce poate greși. */
export function ceasulEditarii(
  nesalvat: boolean, atins: string | null, salvatLa: string | null | undefined,
): { cand: string; nesalvat: boolean } | null {
  // Nesalvat DAR încă neatins — prima randare de după o deschidere adnotată —
  // cade pe ceasul bazei: altfel colțul ar tăcut tocmai la deschidere.
  if (nesalvat && atins) return { cand: atins, nesalvat: true };
  return salvatLa ? { cand: salvatLa, nesalvat: false } : null;
}

export default function Editor() {
  const { projectId = "", code = "" } = useParams();
  /** FĂRĂ PROIECT E MODULUL DIN COLECȚIA MEA (migrarea 0045). Același editor
   *  slujește ambele: desenul e același, doar rutele și întoarcerea diferă. */
  const alMeu = !projectId;
  const inapoi = alMeu ? "/tronsoane" : `/p/${projectId}/templates`;
  const caleaEditorului = (c: string) => (alMeu
    ? `/tronsoane/${encodeURIComponent(c)}/edit`
    : `/p/${projectId}/templates/${encodeURIComponent(c)}/edit`);
  const t = useT();
  const navigate = useNavigate();
  /** MASA DE LUCRU — paleta, pânza, proprietățile, istoricul, tastele. E
   *  aceeași cu a nivelului de soluție (v. `editor/Masa`); aici rămâne doar ce
   *  ține de ȘABLON: de unde vine, cum se scrie ciorna, publicarea, etichetele,
   *  asistentul. */
  const [meta, setMeta] = useState<api.Tronson | null>(null);
  const { potScrie: potScrieProiect } = useRolProiect();
  /** CINE POATE SCRIE AICI.
   *
   *  Pe colecția omului nu există niciun proiect, deci `useRolProiect` —
   *  singura sursă de până acum — întoarce implicitul lui, `false`, și
   *  ecranul pornea doar-citire: nu se putea trage niciun perete, nici muta o
   *  axă. Adevărul îl spune SERVERUL, pe modul (`pot_scrie`, din grantul de
   *  acces); cât nu s-a întors încă, se presupune că da — altfel uneltele ar
   *  clipi stinse la fiecare deschidere. Scrierea o refuză oricum serverul
   *  dacă n-ai voie. */
  const potScrie = alMeu
    ? ((meta as { pot_scrie?: boolean } | null)?.pot_scrie ?? true)
    : potScrieProiect;
  const masa = useMasa({ potScrie, cheieCalc: [projectId, code] });
  const { doc, docRef } = masa;
  const [salvat, setSalvat] = useState<string>("");   // documentul de la ultima scriere
  /** ETICHETELE MODULULUI, ținute lângă document și nu în el.
   *
   *  Nu sunt geometrie: nu schimbă nicio arie, nu se derivă nimic din ele, nu
   *  ating amprenta — deci n-au ce căuta în `doc` și nu trec prin «Salvează».
   *  Puse într-o ciornă, ar fi rămas nevăzute până la publicare și s-ar fi
   *  pierdut la o renunțare. Se scriu pe loc, cu ruta lor.
   *
   *  De-aia nu intră nici în `nesalvat`: o etichetă pusă nu face documentul
   *  nesalvat, fiindcă nu e în el. */
  const [etichete, setEtichete] = useState<string[]>([]);
  /** Vocabularul proiectului — din ce se poate alege. */
  const [posibileEtichete, setPosibileEtichete] = useState<api.Eticheta[]>([]);
  /** Panoul «cine mai are modulul» e deschis. */
  const [deImpartit, setDeImpartit] = useState(false);
  const [scriuEtichete, setScriuEtichete] = useState(false);
  /** Fereastra «Tronson nou». Deschisă, e singura întrebare de pe ecran: până
   *  nu se alege categoria, modulul nou nu există. */
  const [nou, setNou] = useState(false);
  /** Fereastra de redenumit e deschisă? Cerută în editor anume
   *  (26.10.2026), ca numele să se poată îndrepta fără drum înapoi
   *  în colecție — v. `DialogRedenumire`. */
  const [redenumesc, setRedenumesc] = useState(false);
  /** CE A FĂCUT ULTIMUL BUTON CARE A SCRIS — v. `editor/scriere.ts`. */
  const [scris, setScris] = useState<Scriere | null>(null);
  /** What the catalogue flags in the module, from the last open or write.
   *  Shown, never blocking: the site plan composes a module under a rule
   *  anyway, and only broken data stops it (team ruling, 03.11.2026). */
  const [avertismente, setAvertismente] = useState<Avertisment[]>([]);
  const [vadAvertismente, setVadAvertismente] = useState(false);
  /** The panel's "don't show on save" box (`warningsOnSave`); held here too,
   *  so it stays ticked for the session when the browser will not keep it. */
  const [tacut, setTacut] = useState(false);
  const tacutInSesiune = useRef(false);
  const scopulModulului = alMeu ? "al_meu" : projectId;
  useEffect(() => {
    setTacut(tacutInSesiune.current || isMuted(scopulModulului, code, avertismente));
  }, [avertismente, scopulModulului, code]);
  const [lucrez, setLucrez] = useState(false);
  /** Se randează planșa PDF — butonul stă stins cât ține. */
  const [exportez, setExportez] = useState(false);
  const [eroare, setEroare] = useState("");
  const nesalvat = doc != null && JSON.stringify(doc) !== salvat;

  /** CEASUL ULTIMEI ATINGERI, cât desenul e nesalvat. Colțul arată ora
   *  editării, iar cerința e în trei bucăți (arhitect, 21.10.2026): «să se
   *  actualizeze ora pe măsură ce editez; dacă salvăm, salvează acea oră și o
   *  ține minte; dacă nu salvăm, revine la ultima oră salvată».
   *
   *  A TREIA SE ÎNTÂMPLĂ SINGURĂ, și de-aia ceasul ăsta e ȚINUT DEOPARTE de
   *  cel salvat în loc să-l înlocuiască: cât `nesalvat` e adevărat se arată
   *  el, iar în clipa în care documentul se întoarce la cel scris — un ⌘Z până
   *  la capăt, sau o salvare — se arată iar al bazei. Scris peste `meta`, ora
   *  locală n-ar mai fi avut de unde să se întoarcă. */
  const [atins, setAtins] = useState<string | null>(null);
  useEffect(() => {
    // NUMAI CÂT E NESALVAT: pe un desen curat n-avem ce stampila, iar efectul
    // ar fi pus ora deschiderii peste ora scrierii adevărate.
    if (nesalvat) setAtins(new Date().toISOString());
  }, [doc, nesalvat]);

  // ---- încărcare --------------------------------------------------------- #
  /** One counter for every load, so a slower older answer loses to a newer
   *  choice or a changed `code`. */
  const cerereRef = useRef(0);
  const [reimprospatare, setReimprospatare] = useState(0);
  const aplicaDeschiderea = (r: api.Tronson & { doc: unknown }) => {
    // AXELE SE SCRIU LA DESCHIDERE, pe geometria încă neatinsă: de-acum
    // fiecare zid are două capete cu nume, deci intră în graf și vecinii îl
    // urmează când e mutat. `salvat` ia documentul ADNOTAT, nu pe cel
    // primit — altfel modulul s-ar deschide direct pe «nesalvat», pentru o
    // adnotare pe care n-a cerut-o nimeni.
    // MOBILA NU SE MAI DEDUCE LA DESCHIDERE. Se scria aici, în fiecare cameră
    // fără piese, mobilarea felului ei (`cuMobilaScrisa`) — deci modulul se
    // deschidea cu mobilă pe care catalogul n-o are, iar prima salvare o
    // scria acolo. Acum se deschide cu ce are; mobila se pune cu bagheta
    // sau de mână (Stefan, 23.09.2026: «dezactivează și asta»).
    // «L»-URILE SE RUP ÎNAINTE DE ORICE. Un zid de colț desenat ca un
    // singur poligon în L n-are ax, grosime sau lungime — toate trei
    // presupun un dreptunghi —, deci tras sau rotit se strică. Rupt în
    // două la deschidere, desenul e același și fiecare bucată se poartă ca
    // orice zid. V. `model::cuLuriDesfacute`.
    const cuAxe = gataDeDeschis(r.doc as TronsonDoc);
    // REPERUL PENTRU CAMERE, luat pe geometria de la deschidere. Camerele
    // derivate nu ies la virgulă la fel ca cele transcrise, iar ariile
    // apartamentelor sunt măsurate, nu recalculate din desen. Ținând minte
    // cum arăta fiecare bucată la început — ȘI CE CAMERĂ ERA SCRISĂ PE EA —,
    // o cameră pe care n-a atins-o nimeni rămâne scrisă cuvânt cu cuvânt și
    // aria ei declarată nu se mișcă. Nici după un zid dus și adus înapoi:
    // v. `reperulCamerelor`.
    masa.deschide(cuAxe);
    setSalvat(JSON.stringify(cuAxe));
    setMeta(r);
    setEtichete(r.etichete ?? []);
    setReimprospatare((n) => n + 1);
    setAvertismente([]);
    setVadAvertismente(false);
    const cerere = cerereRef.current;
    api.avertismenteTronson(cuAxe)
      .then((a) => { if (cerere === cerereRef.current) setAvertismente(a.avertismente ?? []); })
      .catch(() => {});
  };

  /** After a write: the list is the written drawing's. A non-empty one is
   *  announced by a toast whose «Vezi» opens the panel, so nothing has to be
   *  closed; a module muted for these warnings saves silently, and the badge
   *  in the header still opens the panel. */
  const dupaScriere = (lista: Avertisment[] | undefined, urmarea: "ciorna" | "publicat") => {
    const l = lista ?? [];
    setAvertismente(l);
    if (!l.length) {
      setMuted(scopulModulului, code, null);
      tacutInSesiune.current = false;
    }
    if (shouldAnnounce(scopulModulului, code, l) && !tacutInSesiune.current) {
      arataToast(tn(urmarea === "ciorna" ? "editor.avertismente_toast_ciorna#n"
                                         : "editor.avertismente_toast_publicat#n", l.length), "info",
                 undefined, { eticheta: tr("editor.avertismente_vezi"),
                              laClic: () => setVadAvertismente(true) });
    }
  };

  /** Opens one version through the same steps as opening the editor. Whether
   *  unsaved work may be left is asked first, by the picker. */
  const deschideVersiunea = async (versiune: number) => {
    const cerere = ++cerereRef.current;
    setEroare("");
    setScris(null);
    try {
      const r = await (alMeu ? api.getTronsonulMeu(code, { ciorna: true, version: versiune })
                             : api.getTronson(projectId, code, { ciorna: true, version: versiune }));
      if (cerere === cerereRef.current) aplicaDeschiderea(r);
    } catch (e) {
      if (cerere === cerereRef.current) setEroare(String((e as Error)?.message ?? e));
    }
  };

  useEffect(() => {
    let viu = true;
    const cerere = ++cerereRef.current;
    // `?ciorna=1`: editorul deschide LUCRUL, nu ce rulează — dacă cineva a
    // lăsat o ciornă ieri, ea se deschide, nu versiunea publicată.
    (alMeu ? api.getTronsonulMeu(code, { ciorna: true })
           : api.getTronson(projectId, code, { ciorna: true }))
      .then((r) => {
        if (!viu || cerere !== cerereRef.current) return;
        aplicaDeschiderea(r);
      })
      .catch((e) => viu && cerere === cerereRef.current && setEroare(String(e?.message ?? e)));
    // Vocabularul, în același pas cu modulul: apăsarea pe «+ etichetă» trebuie
    // să arate din prima din ce se alege, nu o listă goală cât ține o cerere.
    // Un vocabular gol e un răspuns, deci eroarea lui nu oprește deschiderea.
    (alMeu ? api.listEtichetleMele() : api.listEtichete(projectId))
      .then((r) => viu && setPosibileEtichete(r.etichete))
      .catch(() => viu && setPosibileEtichete([]));
    return () => { viu = false; };
  }, [projectId, alMeu, code]);

  /** Scrie mulțimea de etichete a modulului, apoi recitește vocabularul.
   *
   *  OPTIMIST PE ECRAN, dar din ce a răspuns serverul: lista se pune din
   *  răspuns (curățată, fără duplicate, sortată ca în colecție), nu din ce s-a
   *  trimis — altfel «Social » scris cu un spațiu ar rămâne pe ecran altfel
   *  decât e în bază. La o cădere, starea se întoarce la ce era.
   *
   *  Vocabularul se recitește fiindcă un nume nou tocmai s-a născut în el, iar
   *  numărătorii celorlalte s-au schimbat. */
  const scrieEtichetele = async (cerute: string[]) => {
    const inainte = etichete;
    setScriuEtichete(true);
    setEroare("");
    try {
      const r = await (alMeu ? api.puneEticheteleMele(code, cerute)
                             : api.puneEtichetele(projectId, code, cerute));
      setEtichete(r.etichete);
      const v = await (alMeu ? api.listEtichetleMele()
                             : api.listEtichete(projectId)).catch(() => null);
      if (v) setPosibileEtichete(v.etichete);
    } catch (e) {
      setEtichete(inainte);
      setEroare(String((e as Error)?.message ?? e));
    } finally {
      setScriuEtichete(false);
    }
  };

  /** CE POATE CERE ASISTENTUL DE LA EDITOR — v. `asistent/unelte.ts`.
   *
   *  Obiectul e STABIL (`useMemo` pe cod, funcții care citesc din `ref`), și
   *  trebuie să rămână așa: din el se compune lista de unelte, iar lista pleacă
   *  o dată, la deschiderea conversației. Refăcut la fiecare desenare, ar fi
   *  însemnat o listă nouă de unelte la fiecare tastă apăsată — adică prefixul
   *  de cache al modelului aruncat de fiecare dată.
   *
   *  Asistentul trece prin ACELEAȘI porți ca mâna: `aplica` e cea a panoului,
   *  deci schimbările lui intră în istoric și se anulează cu ⌘Z. */
  // Asistentul citește masa prin `ref`-uri, ca obiectul lui să rămână stabil
  // (v. nota de mai sus): funcțiile mesei se refac la fiecare desenare.
  const aplicaRef = useRef(masa.aplica);
  aplicaRef.current = masa.aplica;
  const anuleazaRef = useRef(masa.anuleaza);
  anuleazaRef.current = masa.anuleaza;
  const arataRef = useRef(masa.arata);
  arataRef.current = masa.arata;
  const { alesRef, calcRef, schimbaCalculRef } = masa;
  const metaRef = useRef(meta);
  metaRef.current = meta;
  const ctxAsistent = useMemo<ContextEditor>(() => ({
    doc: () => docRef.current,
    // SCHIMBAREA ASISTENTULUI SE ȘI VEDE, nu doar se întâmplă (v.
    // `editor/aratare.ts`). Documentul se schimbă pe loc, ca până acum —
    // arătarea e numai a ecranului, deci uneltele care citesc `doc()` imediat
    // după `aplica` găsesc documentul nou, nu unul pe drum.
    aplica: (f, opt) => {
      const inainte = docRef.current;
      aplicaRef.current(f, opt);
      const dupa = docRef.current;
      if (inainte && dupa && inainte !== dupa) arataRef.current(inainte, dupa);
    },
    ales: () => alesRef.current,
    anuleaza: () => anuleazaRef.current(),
    meta: () => ({ cod: code, versiune: metaRef.current?.version,
                   stare: metaRef.current?.status }),
    // HÂRTIA DE CALC e a mesei, nu a documentului, deci nu poate trece prin
    // `aplica`: n-are ce căuta în istoric și n-ar avea ce anula ⌘Z. Ajunge la
    // asistent pe ușa ei, cu aceleași două gesturi pe care le are și mâna —
    // s-o vadă și s-o așeze.
    calc: () => calcRef.current,
    puneCalc: (c) => schimbaCalculRef.current(c),
  }), [code]);

  /** Avertizează la ieșire cât timp e ceva nescris.
   *
   *  DOUĂ CĂI, fiindcă sunt două feluri de plecare. `beforeunload` prinde fila
   *  închisă și reîncărcarea — dar NU navigarea din aplicație, care nu trece
   *  prin browser: pe acolo se pierdea tot ce nu era salvat, fără o vorbă (v.
   *  `nescris.ts`). Întrebarea din registru o acoperă. */
  useEffect(() => {
    if (!nesalvat) return;
    const la = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener("beforeunload", la);
    tineNescrisul(() => window.confirm(tr("editor.pleci_nesalvat")));
    return () => {
      window.removeEventListener("beforeunload", la);
      tineNescrisul(null);
    };
  }, [nesalvat]);

  // ---- scriere ----------------------------------------------------------- #
  //
  // CE SE TRIMITE E `docRef.current`, NU `doc`. Și diferența e chiar un pierdut
  // de modificări, măsurat pe câmpurile din panou: un câmp de cifre se dă
  // documentului la BLUR, iar clicul pe «Salvează ciorna» e chiar ce-l scoate
  // din focus. Blurul și clicul cad în aceeași tură de JavaScript, deci `doc`
  // din închiderea butonului e documentul de DINAINTE de cifra tocmai scrisă:
  // se trimitea la server desenul vechi, iar `salvat` se punea pe el. `aplica`
  // scrie `docRef.current` pe loc (v. nota din el), deci ref-ul are și cifra.
  //
  // Și `salvat` se pune pe CE S-A TRIMIS, nu pe ce era pe ecran: altfel o
  // atingere în timpul cererii ar fi fost declarată salvată.
  const salveaza = async () => {
    const doc = docRef.current;
    if (!doc) return;
    // Nimic schimbat = nicio versiune nouă. Fiecare salvare scrie un rând nou
    // în catalog (niciodată un UPDATE), deci o apăsare în gol n-are voie să
    // inventeze o versiune — iar butonul nu e singurul drum până aici.
    if (JSON.stringify(doc) === salvat) return;
    setLucrez(true);
    setEroare("");
    setScris(null);
    try {
      const baza = versiuneaDeBaza(meta);
      const r = await (alMeu ? api.salveazaTronsonulMeu(code, doc, undefined, baza)
                             : api.salveazaTronson(projectId, code, doc, baza));
      setSalvat(JSON.stringify(doc));
      setReimprospatare((n) => n + 1);
      // ȘI ORA SCRIERII, de la BAZĂ: `atins` era ceasul browserului, bun cât
      // ține desenul în mână, dar ce se ține minte trebuie să fie exact ce se
      // va citi la redeschidere (v. `tronsoane._scrie`). Pe rutele care n-o
      // întorc, se păstrează cea de dinainte în loc să se șteargă.
      setMeta((m) => (m ? { ...m, version: r.version, status: "draft",
                            scope: "proiect",
                            editat_la: r.editat_la ?? m.editat_la } : m));
      setScris({ urmarea: "ciorna", versiune: r.version });
      dupaScriere(r.avertismente, "ciorna");
    } catch (e) {
      setEroare(String((e as Error)?.message ?? e));
    } finally {
      setLucrez(false);
    }
  };

  /** Desenul, ca ȘABLON NOU, sub alt cod.
   *
   *  Ciorna scrie o versiune nouă a ACELUIAȘI modul — planurile proiectului o
   *  primesc în locul celui global. Cine pornește de la M2 ca să ajungă la
   *  altceva vrea însă un modul în plus, nu un M2 schimbat.
   */
  const salveazaCaNou = async () => {
    const doc = docRef.current;
    if (!doc) return;
    const cerut = (window.prompt(t("editor.cod_nou"),
                                 `${code}B`) ?? "").trim().toUpperCase();
    if (!cerut) return;
    setLucrez(true);
    setEroare("");
    try {
      const r = await (alMeu ? api.tronsonNouAlMeu(cerut, doc)
                             : api.salveazaTronsonCaNou(projectId, cerut, doc, code));
      // Se trece pe el: altfel ecranul ar arăta mai departe modulul vechi, iar
      // următoarea salvare ar merge tot în el.
      setSalvat(JSON.stringify(doc));
      navigate(caleaEditorului(r.code));
    } catch (e) {
      const m = String((e as Error)?.message ?? e);
      setEroare(/cod_luat/.test(m)
        ? t("editor.cod_luat", { cod: cerut })
        : /cod_invalid/.test(m) ? t("editor.cod_invalid") : m);
    } finally {
      setLucrez(false);
    }
  };

  /** ȘTERGE TRONSONUL — tot ce are proiectul pe codul ăsta.
   *
   *  Serverul șterge toate rândurile proiectului pe cod — ciorne și publicat
   *  deopotrivă —, iar colecția cade înapoi pe modulul global, dacă există
   *  unul (v. `catalog.py::revino_la_global`). Pe un modul «salvat ca nou» nu
   *  există global, deci dispare de tot. Întrebarea de dinainte spune exact
   *  asta, fiindcă nu se poate anula.
   *
   *  Un modul GLOBAL nu se șterge de aici: e comun tuturor proiectelor, iar
   *  editorul e al unuia singur. Butonul rămâne la locul lui, dezactivat, cu
   *  motivul în titlu — altfel ar părea că lipsește. */
  const stergeTronsonul = async () => {
    if (!meta || !potSterge) return;
    // MODULUL PLATFORMEI PLEACĂ PENTRU TOȚI, nu doar din ecranul ăsta: e cel
    // comun, cu care pornesc toate proiectele. Întrebarea o spune pe față,
    // fiindcă e altă faptă decât ștergerea unui modul propriu.
    if (!window.confirm(t(meta.scope === "platforma"
      ? "editor.sterge_tronson_platforma_intrebare"
      : "editor.sterge_tronson_intrebare", { cod: code }))) return;
    setLucrez(true);
    setEroare("");
    try {
      await (alMeu ? api.stergeTronsonulMeu(code)
                   : api.stergeTronson(projectId, code));
      // Nimic nescris de păstrat: ce era pe ecran tocmai a fost șters.
      setSalvat(JSON.stringify(doc));
      navigate(inapoi);
    } catch (e) {
      setEroare(String((e as Error)?.message ?? e));
    } finally {
      setLucrez(false);
    }
  };

  /** REDENUMIREA MUTĂ ȘI RUTA: codul e în adresă, iar lăsată pe cea veche
   *  pagina ar cere la următoarea reîncărcare un modul care nu mai există.
   *  Numele vine gata curățat din dialog (`api.numeCurat`).
   *
   *  CE E NESALVAT RĂMÂNE NESALVAT, dinadins: redenumirea e a RÂNDULUI din
   *  catalog, nu a desenului de pe ecran, iar o salvare pe furiș ar publica
   *  sub alt nume o ciornă pe care n-a cerut-o nimeni. */
  const redenumeste = async (numeNou: string) => {
    if (!meta || numeNou === code) { setRedenumesc(false); return; }
    setLucrez(true);
    setEroare("");
    try {
      await (alMeu ? api.redenumesteTronsonulMeu(code, numeNou)
                   : api.redenumesteTronson(projectId, code, numeNou));
      setRedenumesc(false);
      navigate(caleaEditorului(numeNou), { replace: true });
    } catch (e) {
      const m = String((e as Error)?.message ?? e);
      setEroare(/cod_luat/.test(m) ? t("editor.cod_luat", { cod: numeNou })
        : /cod_invalid/.test(m) ? t("editor.cod_invalid") : m);
    } finally {
      setLucrez(false);
    }
  };

  const publica = async () => {
    const doc = docRef.current;          // v. nota de la `salveaza`
    if (!doc) return;
    setLucrez(true);
    setEroare("");
    setScris(null);
    try {
      // NESCRISUL SE MĂSOARĂ PE REF, din același motiv: cifra scrisă în panou
      // și neieșită încă din câmp e o modificare, iar fără ea publicarea ar
      // trece pe `published` rândul de dinaintea ei.
      let salvata: number | undefined;
      if (cereCiornaIntai({ nesalvat: JSON.stringify(doc) !== salvat,
                            alProiectului: areRandul({ alMeu, scope: meta?.scope }) })) {
        const baza = versiuneaDeBaza(meta);
        const scriere = await (alMeu ? api.salveazaTronsonulMeu(code, doc, undefined, baza)
                                     : api.salveazaTronson(projectId, code, doc, baza));
        salvata = scriere.version;
        setSalvat(JSON.stringify(doc));
        setMeta((m) => (m ? { ...m, version: scriere.version, status: "draft",
                              scope: "proiect" } : m));
      }
      const deTrimis = versiuneaDePublicat({ salvata, deschisa: meta?.version });
      const r = await (alMeu ? api.publicaTronsonulMeu(code, deTrimis)
                             : api.publicaTronson(projectId, code, deTrimis));
      // Another number means today's rules changed the drawing and the server
      // wrote a new version: the one on screen is no longer what was published.
      if (r.version !== deTrimis) {
        await deschideVersiunea(r.version);
      } else {
        setMeta((m) => (m ? { ...m, version: r.version, status: "published",
                              scope: "proiect" } : m));
        setReimprospatare((n) => n + 1);
      }
      setScris({ urmarea: "publicat", versiune: r.version });
      dupaScriere(r.avertismente, "publicat");
    } catch (e) {
      setEroare(String((e as Error)?.message ?? e));
    } finally {
      setLucrez(false);
    }
  };

  if (eroare && !doc) {
    return <div className="ob-pagina"><div className="err">{eroare}</div></div>;
  }
  if (!doc || !masa.plan) {
    return (
      <div className="ob-pagina">
        <p className="note">{t("editor.se_incarca", { cod: code })}</p>
      </div>
    );
  }

  const doarCitire = !potScrie;

  /** Planșa modulului ca PDF sau DWG — v. `viewer/tronson/export/exporta`.
   *  Ia DESENUL DE FAȚĂ (`docRef.current`), cu modificările nesalvate cu tot. */
  const exporta = async (format: FormatExport) => {
    const doc = docRef.current;
    if (!doc) return;
    setExportez(true);
    setEroare("");
    try {
      await exportaTronson(format, doc, meta);
    } catch (e) {
      setEroare(`${t(format === "pdf" ? "export.esuat" : "export.dwg_esuat")} `
        + String((e as Error)?.message ?? e));
    } finally {
      setExportez(false);
    }
  };
  /** E al meu — deci se poate șterge și da mai departe. Pe colecția unui
   *  proiect, «al meu» înseamnă rândul proiectului (vechiul `scope`). */
  const alMine = alMeu ? meta?.scope === "al_meu" : meta?.scope === "proiect";
  /** CINE SE POATE ȘTERGE, și de ce nu e același lucru cu «al meu».
   *
   *  Modulele PLATFORMEI — cele șapte cu care pornește orice proiect — se
   *  puteau șterge de pe server dintotdeauna (`api/tronsoane::sterge` nu cere
   *  decât să nu fie al ALTCUIVA), dar ecranul le ținea închise de două ori:
   *  butonul era stins, iar mâna lui se întorcea din prima linie dacă
   *  `scope` nu era «proiect» — ceea ce în colecția omului nu e niciodată. Deci
   *  pe colecția personală ștergerea nu funcționa deloc, oricare ar fi fost
   *  modulul (cerut 01.10.2026: «my colleagues are trying to delete some of
   *  them but they ran into these issues»).
   *
   *  CE RĂMÂNE ÎNCHIS: modulul ÎMPĂRȚIT cu mine. Acolo serverul răspunde 403 și
   *  are dreptate — a primi un modul nu e o cale prin care i-l poți lua
   *  celuilalt. */
  const potSterge = alMeu
    ? (meta?.scope === "al_meu" || meta?.scope === "platforma")
    : meta?.scope === "proiect";

  return (
    <div className="ob-ed">
      {/* ---- bara de sus ---------------------------------------------- */}
      <header className="ob-ed-sus">
        <button className="ob-ed-icon"
                onClick={() => potPleca()
                  && navigate(inapoi)}
                title={t("editor.inapoi")}><Icoana nume="inapoi" /></button>
        <b className="ob-ed-nume">{doc.id}</b>
        <span className="ob-ed-sub">{doc.label}</span>
        {meta && (
          <VersionPicker code={code} projectId={alMeu ? undefined : projectId}
                         meta={meta} reimprospatare={reimprospatare}
                         dezactivat={lucrez} onChoose={deschideVersiunea} />
        )}
        <div className="ob-ed-sp" />
        <div className="ob-ed-actiuni">
          {/* ANULAREA ȘI REFACEREA SUNT DOAR PE TASTE — ⌘Z / ⇧⌘Z pe Mac,
              Ctrl+Z / Ctrl+Y (sau Ctrl+⇧+Z) pe Windows. Cerut așa: două
              butoane care în nouă cazuri din zece stau stinse ocupau capul
              barei, adică locul cel mai scump de pe ecran, iar gestul se face
              oricum din taste de către oricine a desenat vreodată un plan.
              Teancul rămâne întreg (`Istoric`), doar butoanele au plecat. */}
          <ComutatoareMasa m={masa} laEroare={setEroare} />
          <span className="ob-ed-stare">
            {nesalvat ? t("editor.nesalvat")
              : lucrez ? "…" : t("editor.salvat")}
          </span>
          {/* STINS CÂND N-AI CE SALVA, și cifra scrisă în panou nu se pierde
              din asta: apăsarea pe buton scoate câmpul din focus, câmpul își
              dă valoarea documentului la ieșirea din focus, iar butonul e deja
              aprins când clicul cade — măsurat în editor, 17.09.2026. Ce se
              trimite e `docRef.current`, tocmai ca ordinea asta să nu conteze
              (v. `salveaza`). */}
          <button className="btn" onClick={salveaza}
                  disabled={lucrez || doarCitire || !nesalvat}>
            {t("editor.salveaza_ciorna")}
          </button>
          <button className="btn" onClick={salveazaCaNou} disabled={lucrez || doarCitire}
                  title={t("editor.salveaza_ca_nou_titlu")}>
            {t("editor.salveaza_ca_nou")}
          </button>
          {/* REDENUMEȘTE, lângă salvări: e tot o faptă asupra RÂNDULUI din
              catalog, nu asupra desenului. Stins fără drept de scriere și cât
              timp pleacă altă cerere. */}
          <button className="btn" onClick={() => setRedenumesc(true)}
                  disabled={lucrez || doarCitire || !meta}
                  title={t("colectie.redenumeste_titlu", { cod: code })}>
            {t("editor.redenumeste")}
          </button>
          {/* «TRONSON NOU» NU E «SALVEAZĂ CA NOU», și de-aia stau alături.
              Al doilea pune DESENUL DE FAȚĂ deoparte sub alt cod — pornești de
              la M2 ca să ajungi la altceva. Ăsta începe de la zero: alegi
              categoria și primești amprenta ei goală. Cine voia un modul care
              n-are nimic din M2 avea de șters cincizeci și trei de ziduri
              înainte de prima linie a lui. */}
          <button className="btn" onClick={() => setNou(true)}
                  disabled={lucrez || doarCitire}
                  title={t("nou.deschide_titlu")}>
            {t("nou.deschide")}
          </button>
          {/* ȘTERGEREA TRONSONULUI stă mereu lângă salvare. Stinsă numai pe
              modulul ÎMPĂRȚIT cu mine — v. `potSterge`. */}
          <button className="btn btnd" onClick={stergeTronsonul}
                  disabled={lucrez || doarCitire || !potSterge}
                  title={t(doarCitire ? "comun.doar_citire"
                    : !potSterge ? "editor.sterge_tronson_impartit_titlu"
                    : meta?.scope === "platforma"
                      ? "editor.sterge_tronson_platforma_titlu"
                      : "editor.sterge_tronson_titlu")}>
            {t("editor.sterge_tronson")}
          </button>
          {/* ÎMPĂRȚIREA, lângă publicare: sunt cele două feluri în care un
              modul iese din mâna ta. Numai pe cele ale tale — un editor scrie
              modulul, nu-l dă mai departe. */}
          {alMeu && alMine && (
            <button className="btn" onClick={() => setDeImpartit(true)}>
              {t("colectie.imparte")}
            </button>
          )}
          {/* EXPORTUL (PDF, DWG), tot în grupul «iese din mâna ta». Ia
              DESENUL DE FAȚĂ (`docRef.current`), nu ultima scriere: planșa e
              ce vezi, cu modificările nesalvate cu tot — exact ca o
              imprimare. Aprins și la doar-citire, fiindcă nu scrie nimic. */}
          <MeniuExport laExport={(f) => { void exporta(f); }}
                       lucrez={exportez} disabled={!doc} />
          {avertismente.length > 0 && (
            <button className="btn ob-ed-avertisment"
                    onClick={() => setVadAvertismente((v) => !v)}
                    title={t("editor.avertismente_titlu")}>
              ⚠ {avertismente.length}
            </button>
          )}
          <button className="btn btnp" onClick={publica}
                  disabled={lucrez || doarCitire}>{t("editor.publica")}</button>
        </div>
      </header>

      {/* «TRONSON NOU», peste editor. Modulul se scrie pe server și pagina se
          mută pe el — deci ce e nesalvat aici s-ar pierde, și se întreabă ca la
          orice altă plecare (v. `nescris.ts`). Întrebarea vine DUPĂ scriere: ce
          s-a cerut s-a făcut oricum, iar dacă omul rămâne, modulul nou îl
          așteaptă în colecție. */}
      {deImpartit && (
        <ImparteModul cod={code} laInchidere={() => setDeImpartit(false)} />
      )}

      {redenumesc && (
        <DialogRedenumire cod={code} lucrez={lucrez}
                          laRenunta={() => setRedenumesc(false)}
                          laNume={(n) => { void redenumeste(n); }} />
      )}
      {nou && (
        <TronsonNou projectId={projectId}
                    laInchidere={() => setNou(false)}
                    laPornit={(_c, cerere) => { setNou(false); void cerere.catch(() => {}); }}
                    laCreat={(c) => {
                      setNou(false);
                      if (potPleca()) {
                        navigate(
                          caleaEditorului(c));
                      }
                    }} />
      )}

      <MasaDeLucru m={masa} doarCitire={doarCitire} fel="tronson"
                   /* CÂND A FOST EDITAT ULTIMA OARĂ. O editare scrie o
                      VERSIUNE NOUĂ, deci `editat_la` e `created_at` al
                      versiunii curente — ceasul ultimei scrieri, fără coloană
                      nouă și fără nimic de completat pe modulele vechi, care
                      îl au deja. Cu ORA, nu doar ziua: cerut «data și ora», iar
                      pe un modul atins de mai multe ori într-o zi ziua singură
                      nu spune nimic. */
                   editatLa={(() => {
                     const c = ceasulEditarii(nesalvat, atins,
                                              meta?.editat_la);
                     if (!c) return undefined;
                     return tr(c.nesalvat ? "editor.editat_la_nesalvat"
                                          : "editor.editat_la", {
                       cand: `${data(c.cand, {
                         day: "2-digit", month: "2-digit", year: "numeric",
                       })} ${ora(c.cand)}`,
                     });
                   })()}
                   etichete={{
                     etichete, posibile: posibileEtichete, lucrez: scriuEtichete,
                     laSchimbare: (et) => { void scrieEtichetele(et); },
                   }} />

      {/* ---- ce s-a scris ----------------------------------------------
           Urmarea ultimei apăsări, cât e ceva de spus, și lista catalogului
           când e cerută. Desenul nu e refuzat nicăieri (Stefan, 19.09.2026):
           lista spune doar ce nu se leagă pentru planul de situație. */}
      {(eroare || scris || (vadAvertismente && avertismente.length > 0)) && (
        <footer className="ob-ed-probleme">
          {vadAvertismente && avertismente.length > 0 && (
            <div className="ob-ed-avertismente">
              <div className="ob-ed-urmarea">
                <b>{t("editor.avertismente_titlu")}</b>
                <i>{t("editor.avertismente_sfat")}</i>
                <label className="ob-ed-tacut">
                  <input type="checkbox" checked={tacut} onChange={(e) => {
                    const pe = e.target.checked;
                    const pastrat = setMuted(scopulModulului, code, pe ? avertismente : null);
                    tacutInSesiune.current = pe && !pastrat;
                    setTacut(pe);
                  }} />
                  {t("editor.avertismente_tacute")}
                </label>
                <button onClick={() => setVadAvertismente(false)}
                        title={t("editor.inchide")}>
                  <Icoana nume="inchide" />
                </button>
              </div>
              <ul>
                {avertismente.map((a, i) => <li key={i}>{textulAvertismentului(a)}</li>)}
              </ul>
            </div>
          )}
          {scris && (() => {
            const a = antetul(scris);
            return (
              <div className="ob-ed-urmarea">
                <b>{a.titlu}</b>
                <i>{a.sfat}</i>
                <button onClick={() => setScris(null)}
                        title={t("editor.inchide")}>
                  <Icoana nume="inchide" />
                </button>
              </div>
            );
          })()}
          {eroare && <div className="err">{eroare}</div>}
        </footer>
      )}

      {/* ---- asistentul, în colțul din dreapta jos ---------------------- */}
      {/* IMPORTUL NU TRECE PE-AICI. A trecut o zi (22.09.2026): modulul născut
          «dintr-o imagine» deschidea singur discuția și cerea asistentului să
          redeseneze planul, tur după tur. Acum desenul vine gata de la lanțul
          de import (`importer/plan/`), iar asistentul rămâne ce era — mâna
          repede a omului peste un plan care există deja. */}
      <Asistent projectId={projectId} cod={code} potScrie={potScrie}
                ctx={ctxAsistent} />
    </div>
  );
}